/**
 * Session Manager
 * Handles session creation and termination via REST API
 */

import { SessionError } from '../errors/VoiceMateErrors.js';

export class SessionManager {
  constructor(config, logger) {
    this.config = config;
    this.logger = logger;
    this.sessionId = null;
    this.websocketUrl = null;
  }

  /**
   * Create a new session
   * @returns {Promise<Object>} Session data
   * @throws {SessionError} If session creation fails
   */
  async createSession() {
    const url = `${this.config.serverUrl}/api/session/create`;

    this.logger.info('Creating session...', {
      botNumber: this.config.botNumber,
      transport: this.config.transport
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.config.customHeaders
        },
        body: JSON.stringify({
          bot_number: this.config.botNumber,
          from_number: this.config.userNumber,
          transport: this.config.transport,
          language: this.config.language
        })
      });

      if (!response.ok) {
        let errorDetails;
        try {
          errorDetails = await response.json();
        } catch (e) {
          errorDetails = { message: `HTTP ${response.status}` };
        }

        throw new SessionError(
          errorDetails.error || `Session creation failed with status ${response.status}`,
          {
            status: response.status,
            details: errorDetails
          }
        );
      }

      const data = await response.json();

      this.sessionId = data.session_id;
      this.websocketUrl = data.websocket_url;

      this.logger.info('Session created:', {
        sessionId: this.sessionId,
        websocketUrl: this.websocketUrl
      });

      return data;

    } catch (error) {
      this.logger.error('Session creation failed:', error);

      if (error instanceof SessionError) {
        throw error;
      }

      throw new SessionError('Failed to create session', {
        cause: error.message,
        originalError: error
      });
    }
  }

  /**
   * Terminate the current session
   * @returns {Promise<void>}
   */
  async terminateSession() {
    if (!this.sessionId) {
      return;
    }

    const url = `${this.config.serverUrl}/api/session/${this.sessionId}`;

    try {
      await fetch(url, {
        method: 'DELETE',
        headers: this.config.customHeaders
      });

      this.logger.info('Session terminated:', this.sessionId);

    } catch (error) {
      this.logger.warn('Session termination failed:', error);
    } finally {
      this.sessionId = null;
      this.websocketUrl = null;
    }
  }

  /**
   * Get current session information
   * @returns {Object} Session info
   */
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      websocketUrl: this.websocketUrl
    };
  }
}
